import { HttpClient, HttpContext, HttpParams, HttpResponse } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { CASETYPE_STATUS_CATEGORY__ALL, CaseData } from "@vp/models";
import { API_BASE_URL } from "@vp/shared/tokens";
import { isNullOrUndefined } from "@vp/shared/utilities";
import { Operation } from "rfc6902";
import { Observable, throwError } from "rxjs";
import { CaseDataFilter } from "../models/case-data-filter";

@Injectable({
  providedIn: "root"
})
export class CaseFilterApiService {
  constructor(
    @Inject(API_BASE_URL) private apiBaseUrl: string,
    private readonly _http: HttpClient
  ) {}

  filteredCases = (
    filter: Readonly<Partial<CaseDataFilter>>,
    feature: string | undefined,
    httpContext?: HttpContext
  ): Observable<HttpResponse<CaseData[]>> => {
    if (!feature) {
      return throwError("Feature is required");
    }

    let apiURL = `${this.apiBaseUrl}/cases/?take=${filter.take}&skip=${filter.skip}`;

    if (filter.status && !filter.status.includes(CASETYPE_STATUS_CATEGORY__ALL.friendlyId)) {
      const statuses = filter.status.map(x => `&filter=status.friendlyId=${x}`);
      apiURL = apiURL + statuses.join("");
    }

    if (filter.filters?.length) {
      for (let i = 0; i < filter.filters.length; i++) {
        apiURL = apiURL + `&filter=${filter.filters[i]}`;
      }
    }

    if (filter.caseTypeIds) {
      for (let i = 0; i < filter.caseTypeIds.length; i++) {
        apiURL = apiURL + `&filter=caseType.caseTypeId=${filter.caseTypeIds[i]}`;
      }
    }

    if (filter.groupIds) {
      for (let i = 0; i < filter.groupIds.length; i++) {
        apiURL = apiURL + `&filter=groups.groupId=${filter.groupIds[i]}`;
      }
    }

    if (filter.departmentIds) {
      for (let i = 0; i < filter.departmentIds.length; i++) {
        apiURL = apiURL + `&filter=department.departmentId=${filter.departmentIds[i]}`;
      }
    }

    if (filter.rangeField && filter.startDate) {
      apiURL = apiURL + `&range=${filter.rangeField}>=${filter.startDate}`;
    }

    if (filter.rangeField && filter.endDate) {
      apiURL = apiURL + `&range=${filter.rangeField}<=${filter.endDate}`;
    }

    if (filter.search) {
      apiURL = apiURL + `&search=${filter.search}`;
    }
    if (filter.sort) {
      apiURL = apiURL + `&sort=${filter.sort}`;
    }
    if (filter.sortDir) {
      apiURL = apiURL + `&sortDir=${filter.sortDir}`;
    }
    if (!isNullOrUndefined(filter.unassigned)) {
      apiURL = apiURL + `&unassigned=${filter.unassigned}`;
    }
    if (!isNullOrUndefined(filter.completed)) {
      apiURL = apiURL + `&completed=${filter.completed}`;
    }
    if (!isNullOrUndefined(filter.cancelled)) {
      apiURL = apiURL + `&cancelled=${filter.cancelled}`;
    }
    if (!isNullOrUndefined(filter.archived)) {
      apiURL = apiURL + `&archived=${filter.archived}`;
    }

    if (!isNullOrUndefined(filter.unresolved)) {
      apiURL += `&unresolved=${filter.unresolved}`;
    }

    if (!isNullOrUndefined(filter.unread)) {
      apiURL += `&unread=${filter.unread}`;
    }

    if (!isNullOrUndefined(filter.followUpCount)) {
      apiURL += `&followUpCount=${filter.followUpCount}`;
    }

    if (filter.tagIds && filter.tagIds?.length > 0) {
      filter.tagIds.forEach((tagId: string) => (apiURL += `&tag=${tagId}`));
    }
    if (filter.recordData && filter.recordData?.length > 0) {
      filter.recordData.forEach((param: string) => (apiURL += `&recordData=${param}`));
    }

    return this._http.get<CaseData[]>(apiURL, {
      observe: "response",
      context: httpContext
    });
  };

  getCase(caseId: string, caseTypeId: string | null = null): Observable<CaseData> {
    const apiURL = `${this.apiBaseUrl}/case/${caseId}`;
    let params = new HttpParams();
    if (caseTypeId) {
      params = params.append("caseTypeId", String(caseTypeId));
    }
    return this._http.get<CaseData>(apiURL, {
      params: params
    });
  }

  patch = (caseId: string, operations: Operation[]) => {
    const apiURL = `${this.apiBaseUrl}/case/${caseId}`;
    return this._http.patch(apiURL, operations);
  };
}
